import type { FC } from 'react';
import React from 'react';
import { MDText } from 'i18n-react';
import noop from 'lodash/noop';

import { Badge } from '@motorway/mw-highway-code';

import { isActivePage, isDidntWinPage, isUnderOfferPage } from 'Components/Account/Purchases/Purchases.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import TimeCountdown from 'Components/shared/TimeCountdown/TimeCountdown';
import { ForSaleCategory } from 'Types/enquiryStates';
import type { VehicleAccountListItem } from 'Types/vehicle';
import { gbpCurrencyFormatter } from 'Utilities/formatters';
import { applyCypressData } from 'Utilities/helpers';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';

import LocalTexts from '../../BidsAndOffersVehicleList.texts';

import styles from './StatusTagGroup.module.scss';

const LocalT = new MDText(LocalTexts);

type StatusTagGroupProps = {
	cypressId: string;
} & Pick<VehicleAccountListItem, 'userBid' | 'soldFor' | 'state'>;

export const StatusTagGroup: FC<StatusTagGroupProps> = ({ cypressId, soldFor, state, userBid }) => {
	const { slug } = useAccountRouter();
	const { isLiveSaleEnabled, todaysSaleEndsAt } = useSaleTimes();

	if (isActivePage(slug) && userBid.type !== 'offer') {
		const isCurrentlyOnSale = state.slug === ForSaleCategory.saleActive;

		return (
			<div className={styles.badgeGroupContainer} {...applyCypressData(cypressId)}>
				<TimeCountdown countdownTime={todaysSaleEndsAt} timeDifference={0} whenCountdownEnds={noop}>
					{({ timeLeft, timeString }) => {
						const isSaleOngoing = timeLeft && isCurrentlyOnSale && isLiveSaleEnabled;

						return (
							<Badge
								text={
									<span className={styles.badgeWrapper}>
										{LocalT.translate(`badges.activeBid.${isSaleOngoing ? 'endsIn' : 'ended'}`, {
											timeLeft: timeString,
										})}
									</span>
								}
								variant="primary"
							/>
						);
					}}
				</TimeCountdown>
			</div>
		);
	}

	if (isUnderOfferPage(slug)) {
		return (
			<div className={styles.badgeGroupContainer} {...applyCypressData(cypressId)}>
				<Badge
					text={<span className={styles.badgeWrapper}>{LocalT.translate(`badges.underOffer`)}</span>}
					variant="primary"
				/>
			</div>
		);
	}

	if (isDidntWinPage(slug) && soldFor) {
		const soldForCurrencyFormat = gbpCurrencyFormatter({ maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(
			soldFor,
		);
		return (
			<div className={styles.badgeGroupContainer} {...applyCypressData(cypressId)}>
				<Badge
					text={
						<span className={styles.badgeWrapper}>
							{LocalT.translate(`badges.didntWin`, { soldFor: soldForCurrencyFormat })}
						</span>
					}
				/>
			</div>
		);
	}

	return <></>;
};
